import { Client } from 'colyseus.js';
import { isDuplicateSessionKick, isMatchAbortKick, isAfkMatchAbortKick } from '../contracts/wsClose';
import { getColyseusWsUrl } from './colyseusUrl';
import {
  SEKA_GAME_COMMAND,
  SEKA_GAME_ANTE_TAKEN_EVENT,
  SEKA_GAME_CHAT_MESSAGE_EVENT,
  SEKA_GAME_STATE_EVENT,
  SEKA_GAME_PRIVATE_HOLE_EVENT,
  SEKA_RESYNC_REQUIRED_EVENT,
  SEKA_MATCH_ABORTED_EVENT,
  SEKA_MATCH_WAITING_RECONNECT_EVENT,
  SEKA_MATCH_RECONNECT_RESUMED_EVENT,
  SEKA_MATCH_RECONNECT_TIMEOUT_EVENT,
  SEKA_GRACE_RESPECT_EVENT,
} from '../contracts/sekaV1';

const EVENTS = {
  GAME_STATE: SEKA_GAME_STATE_EVENT,
  ERROR: 'system/error',
};

/** Colyseus: нормальный выход по leave(true) */
const CLOSE_CODE_CONSENTED = 4000;

const SESSION_PREFIX = 'seka:game:';

/** Dev / явное включение: `REACT_APP_SEKA_GAME_LOG=1` в .env */
function sekaGameLog(event, detail = {}) {
  if (typeof process === 'undefined' || process.env.REACT_APP_SEKA_GAME_LOG !== '1') return;
  // eslint-disable-next-line no-console
  console.log(`[seka-game] ${event}`, detail);
}

function sessionStorageKey(tableId, gameRoomId) {
  return `${SESSION_PREFIX}${String(tableId)}:${String(gameRoomId || '')}`;
}

function readPersistedReconnect(tableId, gameRoomId) {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(sessionStorageKey(tableId, gameRoomId));
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o?.reconnectionToken && typeof o.reconnectionToken === 'string') {
      return o.reconnectionToken;
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedReconnect(tableId, gameRoomId, room) {
  if (typeof sessionStorage === 'undefined' || !room?.reconnectionToken) return;
  try {
    sessionStorage.setItem(
      sessionStorageKey(tableId, gameRoomId),
      JSON.stringify({
        reconnectionToken: room.reconnectionToken,
        at: Date.now(),
      })
    );
  } catch {
    /* quota / private mode */
  }
}

function clearPersistedReconnect(tableId, gameRoomId) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(sessionStorageKey(tableId, gameRoomId));
  } catch {
    /* ignore */
  }
}

/**
 * Colyseus `seka` room with a Socket.IO–like API for existing UI.
 */
export class ColyseusSekaGameTransport {
  /**
   * @param {import('colyseus.js').Room} room
   * @param {import('colyseus.js').Client} colyseusClient
   * @param {object} joinArgs — для fallback join после неудачного reconnect
   */
  constructor(room, colyseusClient, joinArgs) {
    this.room = room;
    this.client = colyseusClient;
    this.joinArgs = joinArgs;
    this.connected = true;
    this.listeners = new Map();
    this._intentionalDisconnect = false;
    this._reconnectAttempt = 0;
    /** @type {number|null} */
    this._lastHandSeq = null;
    this._leaveHandler = (code, reason) => {
      void this._handleRoomLeave(code, reason);
    };
    this.room.onLeave(this._leaveHandler);
    this._wire();
    writePersistedReconnect(joinArgs.tableId, joinArgs.gameRoomId, room);
  }

  /**
   * @param {{ tableId: string|number, user: { id?: string, username?: string }, accessToken?: string, gameRoomId?: string, roomName?: string, skipPersistedReconnect?: boolean }} args
   */
  static async join(args) {
    const client = new Client(getColyseusWsUrl());
    const token = args.accessToken;
    if (token) {
      client.http.authToken = String(token);
    }

    const joinArgs = {
      tableId: String(args.tableId || '')
        .trim()
        .toUpperCase(),
      user: args.user,
      accessToken: token,
      gameRoomId: args.gameRoomId ? String(args.gameRoomId).trim() : '',
      roomName: args.roomName || 'seka',
    };

    const gid = joinArgs.gameRoomId;
    const roomType = ['seka', 'poker', 'belot'].includes(String(joinArgs.roomName))
      ? String(joinArgs.roomName)
      : 'seka';

    /** Старый joinOrCreate без roomId писал токен под ключом `…:tableId:` — не даём перепутать с joinById. */
    clearPersistedReconnect(joinArgs.tableId, '');

    let room = null;

    if (!args.skipPersistedReconnect && gid) {
      const persisted = readPersistedReconnect(joinArgs.tableId, gid);
      if (persisted) {
        try {
          room = await client.reconnect(persisted);
        } catch {
          room = null;
          clearPersistedReconnect(joinArgs.tableId, gid);
        }
      }
    }

    if (!room) {
      const uid = String(args.user?.id ?? '').trim();
      if (!uid) {
        throw new Error('AUTH_USER_REQUIRED');
      }
      const joinOpts = {
        tableId: joinArgs.tableId,
        userId: uid,
        username: String(args.user?.username ?? 'Игрок'),
        ranked: false,
        avatarUrl: String(args.user?.avatarUrl ?? '')
          .trim()
          .slice(0, 12000),
        level: Math.max(1, Math.floor(Number(args.user?.level) || 1)),
      };
      room = gid
        ? await client.joinById(gid, joinOpts)
        : await client.joinOrCreate(roomType, joinOpts);
    }

    return new ColyseusSekaGameTransport(room, client, joinArgs);
  }

  _createEnvelope(command, data = {}) {
    return {
      command,
      meta: {
        commandId: `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        clientVersion: `web-${process.env.REACT_APP_VERSION || 'dev'}`,
        ...(this._lastHandSeq != null ? { sekaKnownHandSeq: this._lastHandSeq } : {}),
      },
      data,
    };
  }

  async _handleRoomLeave(code, reason) {
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* room may be destroyed */
    }

    const token = this.room?.reconnectionToken;

    if (this._intentionalDisconnect) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, intentional: true });
      return;
    }

    if (isDuplicateSessionKick(code, reason)) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      sekaGameLog('duplicate_session_kick', { code, reason });
      this._emitLocal('disconnect', { code, reason, duplicateSession: true });
      return;
    }

    if (isMatchAbortKick(code, reason)) {
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      sekaGameLog('match_abort_kick', { code, reason });
      this._emitLocal('disconnect', {
        code,
        reason,
        matchAborted: true,
        afkMatchAbort: isAfkMatchAbortKick(code, reason),
      });
      return;
    }

    const maxAttempts = 6;
    if (token && code !== CLOSE_CODE_CONSENTED && this._reconnectAttempt < maxAttempts) {
      this._reconnectAttempt += 1;
      const delayMs = Math.min(4000, 200 * this._reconnectAttempt ** 2);
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const newRoom = await this.client.reconnect(token);
        this.room = newRoom;
        this.connected = true;
        this._reconnectAttempt = 0;
        newRoom.onLeave(this._leaveHandler);
        this._wire();
        writePersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId, newRoom);
        sekaGameLog('reconnect_ok', { code, reason });
        this._emitLocal('reconnected', { code, reason });
        return;
      } catch (e) {
        console.warn('[seka-game] reconnect failed, trying full join', e);
      }
    }

    try {
      const joinOpts = {
        tableId: this.joinArgs.tableId,
        userId: String(this.joinArgs.user?.id ?? 'guest'),
        username: String(this.joinArgs.user?.username ?? 'Игрок'),
        ranked: false,
      };
      const gid = this.joinArgs.gameRoomId ? String(this.joinArgs.gameRoomId).trim() : '';
      const roomType = ['seka', 'poker', 'belot'].includes(String(this.joinArgs.roomName))
        ? String(this.joinArgs.roomName)
        : 'seka';
      const newRoom = gid
        ? await this.client.joinById(gid, joinOpts)
        : await this.client.joinOrCreate(roomType, joinOpts);
      this.room = newRoom;
      this.connected = true;
      this._reconnectAttempt = 0;
      newRoom.onLeave(this._leaveHandler);
      this._wire();
      writePersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId, newRoom);
      sekaGameLog('reconnected_via_full_join', { code, reason });
      this._emitLocal('reconnected', { code, reason, fallback: 'join' });
    } catch (e) {
      console.error('[seka-game] full rejoin failed', e);
      this.connected = false;
      clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
      this._emitLocal('disconnect', { code, reason, error: e });
    }
  }

  /** Raw `game/state` payloads (GameTable и др.). */
  onSnapshot(cb) {
    this.room.onMessage(EVENTS.GAME_STATE, cb);
  }

  /** @param {(payload: { userId: string, cards: string[] }) => void} cb */
  onPrivateHole(cb) {
    this.room.onMessage(SEKA_GAME_PRIVATE_HOLE_EVENT, cb);
  }

  _wire() {
    this.room.onMessage(EVENTS.GAME_STATE, (payload) => {
      const round = payload?.game ?? payload?.gameState;
      const hs = round?.handSeq;
      if (hs != null && Number.isFinite(Number(hs))) {
        this._lastHandSeq = Number(hs);
      }

      const table = payload?.table;
      if (table) {
        this._emitLocal('table_update', { table });
      }
      if (table && round) {
        this._emitLocal('game_started', { table, gameState: round });
        this._emitLocal('game_start', {
          tableId: table.id,
          pot: round.pot,
          currentBet: round.currentBet,
        });
      }
      if (round && table) {
        const turn = round.turnSeatIndex ?? round.currentPlayer ?? 0;
        this._emitLocal('player_action', {
          tableId: table.id,
          pot: round.pot,
          currentBet: round.currentBet,
          currentPlayer: turn,
        });
      }
    });

    this.room.onMessage(EVENTS.ERROR, (payload) => {
      this._emitLocal('error', payload || { message: 'Ошибка игры' });
    });

    this.room.onMessage(SEKA_RESYNC_REQUIRED_EVENT, (payload) => {
      this._emitLocal('resync_required', payload || {});
    });

    this.room.onMessage(SEKA_GAME_ANTE_TAKEN_EVENT, (payload) => {
      this._emitLocal('ante_taken', payload || null);
    });

    this.room.onMessage(SEKA_GAME_CHAT_MESSAGE_EVENT, (payload) => {
      this._emitLocal('game_chat_message', payload || null);
    });

    this.room.onMessage(SEKA_MATCH_ABORTED_EVENT, (payload) => {
      this._emitLocal('match_aborted', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_WAITING_RECONNECT_EVENT, (payload) => {
      this._emitLocal('match_waiting_reconnect', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_RECONNECT_RESUMED_EVENT, (payload) => {
      this._emitLocal('match_reconnect_resumed', payload || {});
    });
    this.room.onMessage(SEKA_MATCH_RECONNECT_TIMEOUT_EVENT, (payload) => {
      this._emitLocal('match_reconnect_timeout', payload || {});
    });

    this.room.onMessage(SEKA_GRACE_RESPECT_EVENT, (payload) => {
      this._emitLocal('seka_grace_respect', payload || {});
    });
  }

  _emitLocal(eventName, payload) {
    const subs = this.listeners.get(eventName);
    if (!subs) return;
    subs.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[seka-game]', e);
      }
    });
  }

  on(eventName, cb) {
    if (!this.listeners.has(eventName)) this.listeners.set(eventName, new Set());
    this.listeners.get(eventName).add(cb);
  }

  off(eventName, cb) {
    if (!this.listeners.has(eventName)) return;
    if (!cb) {
      this.listeners.delete(eventName);
      return;
    }
    this.listeners.get(eventName).delete(cb);
  }

  emit(eventName, payload) {
    switch (eventName) {
      case 'start_game':
        this.room.send('command', this._createEnvelope(SEKA_GAME_COMMAND.START, {}));
        return;
      case 'player_move':
        this.room.send(
          'command',
          this._createEnvelope(SEKA_GAME_COMMAND.ACTION, {
            action: payload?.action,
            amount: payload?.amount,
            seatIndex: payload?.seatIndex,
          })
        );
        return;
      case 'PLAYER_MOVE':
        this.room.send(
          'command',
          this._createEnvelope(SEKA_GAME_COMMAND.ACTION, {
            action: payload?.action,
            amount: payload?.amount,
            seatIndex: payload?.seatIndex,
          })
        );
        return;
      case 'BUY_IN':
        this.room.send(
          'command',
          this._createEnvelope(SEKA_GAME_COMMAND.BUY_IN, {
            amount: payload?.amount,
          })
        );
        return;
      case 'READY':
      case 'ready':
        this.room.send('command', this._createEnvelope(SEKA_GAME_COMMAND.READY, {}));
        return;
      case 'SEKA_GRACE_EXIT':
      case 'seka_grace_exit':
        this.room.send('command', this._createEnvelope(SEKA_GAME_COMMAND.SEKA_GRACE_EXIT, {}));
        return;
      case 'SVARKA_CHOICE':
      case 'svarka_choice':
        this.room.send(
          'command',
          this._createEnvelope(SEKA_GAME_COMMAND.SVARKA_CHOICE, {
            participate: Boolean(payload?.participate),
          })
        );
        return;
      case 'REQUEST_RESYNC':
      case 'request_resync':
      case 'RESYNC':
      case 'resync':
        this.room.send('command', this._createEnvelope(SEKA_GAME_COMMAND.RESUME, {}));
        return;
      case 'GAME_CHAT_SEND':
      case 'game_chat_send': {
        const text = String(payload?.text || '').trim();
        if (!text) return;
        this.room.send(
          'command',
          this._createEnvelope(SEKA_GAME_COMMAND.CHAT_SEND, {
            text,
          })
        );
        return;
      }
      case 'social_resolve_invite': {
        const inviteId = String(payload?.inviteId || '').trim();
        const decision = String(payload?.decision || '').trim().toLowerCase();
        if (!inviteId || !decision) return;
        this.room.send(
          'command',
          this._createEnvelope('social/resolve-invite', {
            inviteId,
            decision,
          })
        );
        return;
      }
      case 'social_send_invite': {
        const toUserId = String(payload?.toUserId || payload?.targetUserId || '').trim();
        const mode = String(payload?.mode || '').trim().toLowerCase();
        if (!toUserId || !mode) return;
        this.room.send(
          'command',
          this._createEnvelope('social/send-invite', {
            toUserId,
            mode,
            tableId: String(payload?.tableId || '').trim(),
            stake: Number.isFinite(Number(payload?.stake)) ? Number(payload.stake) : undefined,
            ante: Number.isFinite(Number(payload?.ante)) ? Number(payload.ante) : undefined,
          })
        );
        return;
      }
      default:
        console.warn('[seka-game] unsupported emit:', eventName);
    }
  }

  sendBuyIn(amount) {
    this.room.send(
      'command',
      this._createEnvelope(SEKA_GAME_COMMAND.BUY_IN, {
        amount,
      })
    );
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this.connected = false;
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* ignore */
    }
    clearPersistedReconnect(this.joinArgs.tableId, this.joinArgs.gameRoomId);
    this.room.leave(true);
    /** Не clear listeners здесь: сервер шлёт MATCH_ABORTED почти сразу после leave — иначе `match_aborted` не доходит до GameTable. Снимает `transport.off` в removeTransportUiListeners / unmount. */
  }
}
